import React, { useContext, useState } from 'react'
import { flow } from 'lodash'

import { inject, observer } from 'mobx-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation, useParams, withRouter } from 'react-router'
import queryString from 'query-string'

import * as validations from '../validations/fieldLevelValidations'
import PageLoadingAnimation from '../shared/PageLoadingAnimation'

import { TextFieldControl, RadioGroupControl, AvatarControl } from '../utils'
import { createStyles, Divider, FormControlLabel, Grid, makeStyles, RadioGroup, Theme, Typography, Button } from '@material-ui/core'
import { ParamTopicContext, AuthContext, ParamConversationContext, DialogContext } from '../Providers'
import { IStore, IStories } from '../../store'

/**
 * The cap the counter below reads.
 *
 * ⚠️ It MUST mirror the `validations.maxLength(2500)` rule on the `content`
 * field. It is a second literal rather than the same one because that rule is
 * deliberately being left untouched; if these two ever disagree the counter
 * lies, so change them together.
 *
 * ⚠️ And they do not count the same way. `validations.maxLength` strips
 * whitespace and hyphens before measuring (`fieldLevelValidations.js`), while
 * the server's `validates :content, length: { maximum: 2500 }` measures the
 * raw string. This counter measures RAW, because the raw length is what
 * actually gets a submission rejected. The consequence, stated so it is not
 * mistaken for a bug: on prose with a lot of whitespace the counter can turn
 * red while the submit button is still enabled. The submission then fails and
 * the server's own message is rendered under the field -- which is what the
 * error handling below is for.
 */
const CONTENT_MAX_CHARACTERS = 2500

/**
 * The fields this form actually renders, by the name the server uses for them.
 *
 * 📌 Deliberately NOT run through `camelCase`, which is what the other twelve
 * forms in this app do with server errors (documents/Form.tsx,
 * conversations/Form.tsx and the rest). Those forms name their inputs in
 * camelCase; this one names them in snake_case so the payload can go straight
 * onto a multipart wire that gets no key transform. `camelCase('author_name')`
 * is `authorName`, which is not a field here -- the error would be attached to
 * an input that does not exist and would never appear on screen.
 */
const RENDERED_FIELDS = ['prompt', 'asset', 'headline', 'content', 'author_name', 'author_email']

const Form = ({ onSubmit, stories }: Props & InjectedProps) => {
  const { handleSubmit, errors, formState, control, watch, setError, reset, clearErrors, setValue } = useForm({ mode: 'onChange' })

  const { isDirty, isSubmitting, dirtyFields, isValid } = formState

  const [submitting, setsubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | undefined>(undefined)
  const { topic } = useContext(ParamTopicContext)
  const { conversation } = useContext(ParamConversationContext)
  const { user } = useContext(AuthContext)
  const { showDialog } = useContext(DialogContext)
  const history = useHistory()
  const location = useLocation()
  const { conversationId } = useParams<{ conversationId?: string }>()
  const classes = Styles()

  const { t } = useTranslation()

  /**
   * The submission token off the printed card: `/{slug}/story?k=...`.
   *
   * It is what lets someone who is not signed in and did not RSVP post a
   * story at all, and it is what makes the server file the story
   * `visible: false` pending approval. Read here and passed straight through
   * -- the client never inspects it.
   *
   * ⚠️ It goes on the wire as `story[submission_token]`, and the name is not
   * negotiable. `Api::StoriesController#submission_token` reads exactly
   * `params[:submission_token] || params.dig(:story, :submission_token)`, and
   * `spec/requests/api/stories_request_spec.rb` has a case pinning that
   * `story[token]` is REFUSED. It was deliberately not aliased -- the app
   * already carries `host_token`, `guest_token` and approval tokens, and two
   * names for one credential is how the next mismatch happens.
   */
  // `watch` re-renders on every keystroke, which is what makes the counter
  // below live. It was already destructured off `useForm` and unused.
  const contentLength = ((watch('content') as string) || '').length

  const rawToken = queryString.parse(location.search).k
  const submissionToken = Array.isArray(rawToken) ? rawToken[0] : rawToken

  if (!topic) return <></>

  /**
   * 🔴 Render what the server said. This is the defect this handler exists to
   * fix: `topic.create` already returned `{ error: json }` on a 422 and the
   * form threw it away, so a rejected submission looked like nothing
   * happening at all. On the QR-card route that lands on a stranger at a
   * dinner table with no account and no way to guess what went wrong.
   *
   * The body is Rails' `@story.errors.messages` -- `{ content: ["is too long
   * (maximum is 2500 characters)"] }` -- so the keys are attribute names and
   * the values are arrays. Anything naming a field this form renders goes
   * under that field; anything else (`base`, an attribute with no input, a
   * body that is not even an object) becomes one message above the button,
   * because an error nobody can see is the thing being fixed here.
   */
  const displayServerErrors = (error: any) => {
    const unattached: string[] = []
    let attached = false

    if (error && typeof error === 'object') {
      for (const fieldName of Object.keys(error)) {
        const message = ([] as any[]).concat(error[fieldName]).filter(Boolean).join(', ')
        if (!message) continue

        if (RENDERED_FIELDS.includes(fieldName)) {
          setError(fieldName, { type: 'manual', message })
          attached = true
        } else {
          unattached.push(fieldName === 'base' ? message : `${fieldName.replace(/_/g, ' ')} ${message}`)
        }
      }
    }

    if (unattached.length) {
      setSubmitError(unattached.join(' '))
    } else if (!attached) {
      // Nothing usable came back -- a 403 on a bad card token, a network
      // failure, an HTML error page. Still say something.
      setSubmitError(t('story.submitFailed'))
    }
  }

  const handleSave = handleSubmit((data: any, e) => {
    setsubmitting(true)
    setSubmitError(undefined)
    const { author_name, author_email, ...storyFields } = data
    const storyData = {
      ...storyFields,
      topic_id: topic.id,
      /**
       * Sent only when there is a dinner behind the story. A submission from
       * the topic card has none, and posting `conversation_id: undefined`
       * through `toFormData` would put the literal string "undefined" on the
       * wire rather than leaving the key off.
       */
      ...(conversation?.id ? { conversation_id: conversation.id } : {}),
      ...(submissionToken ? { submission_token: submissionToken } : {}),
      /**
       * Only for a submission with no account behind it. `user_id` is gone
       * from this payload entirely: the server derives the owner from the
       * session, and `user_id` is not on `StoryPolicy::AUTHOR_ATTRIBUTES`, so
       * sending it was at best noise and at worst an invitation to trust it.
       *
       * ⚠️ EVERY key in this object must already be snake_case.
       *
       * The request is multipart, and Rails applies its camelCase ->
       * snake_case parameter transform to JSON bodies ONLY. A multipart part
       * named `story[authorName]` is therefore not renamed on arrival -- it
       * falls past `permit`, the story saves, the request 201s, and the value
       * is gone with nothing to notice.
       *
       * To be exact about the current state, because it is easy to overstate:
       * a camelCase key written here would NOT actually reach the server that
       * way today. `TopicStories.create` calls
       * `StoriesApi.create(snakify(data))`, and that converts it one layer up.
       * Measured, not assumed -- renaming these fields to camelCase leaves
       * every wire-level assertion in the suite green.
       *
       * That rescue is exactly what must not be relied on. `snakify` is a
       * shallow lodash `mapKeys`, so it never reaches a nested object's keys;
       * it lives in one caller, not in `StoriesApi.create`, which applies no
       * transform at all; and an assertion downstream of it cannot see the
       * defect it is supposed to guard. Being snake_case at the source is the
       * property that holds either way, and `Form.test.tsx` asserts it
       * against `snakify`'s INPUT for that reason.
       */
      ...(user ? {} : { author_name, author_email }),
      prompt: t(`story.${data.prompt}`)
    }
    topic.create(storyData).then(({ data: story, error }: FIXME = {}) => {
      if (story) {
        onSubmit && onSubmit()

        /**
         * 🔴 Only go back if there is a "back" that means anything.
         *
         * `goBack()` was right for the conversation-scoped design: a signed-in
         * guest reached this form FROM their conversation page -- the story
         * request email's call to action, or the conversation card -- so
         * returning them there with the confirmation on top was the whole
         * gesture.
         *
         * A card scanner has no history. `/{slug}/story?k=...` is the first
         * page in their tab. `goBack()` there leaves the browser somewhere
         * arbitrary, drops the token out of the URL on the way, and the
         * success dialog is never seen -- so a submission that worked looks
         * exactly like one that failed, on a URL that then correctly answers
         * "Unauthorized" if they reload it. Measured on staging by Aki on the
         * first real run of the QR flow.
         *
         * The branch is on the ROUTE, not on the token: `/:slug/story` is also
         * reachable from the topic page's own button, and staying put with the
         * confirmation on screen is right there too. Only the per-conversation
         * route -- the one that names a conversation, the one already in
         * people's inboxes -- keeps going back, and its behaviour is
         * deliberately untouched.
         */
        if (conversationId) {
          history.goBack()
        }

        showDialog('storyCollectionSuccess', { story, conversation, storyId: story.id })
      } else {
        // Without this the button stays disabled forever on a rejected
        // submission -- much likelier now that the form is reachable without
        // a session, where the server can refuse the token.
        setsubmitting(false)
        displayServerErrors(error)
      }
    })
  })

  /**
   * Wait for the conversation only on the route that names one. On
   * `/:slug/story` there is no conversation to wait for, and the old
   * `!conversation` guard would have held the page on a spinner forever.
   */
  if (conversationId && !conversation) {
    return <PageLoadingAnimation />
  }
  const PROMPT_EXAMPLES = [
    {
      label: <Typography variant='body2'>{`${t('story.prompt_1')}...`}</Typography>,
      value: 'prompt_1'
    },
    {
      label: <Typography variant='body2'>{`${t('story.prompt_2')}...`}</Typography>,
      value: 'prompt_2'
    },
    {
      label: <Typography variant='body2'>{`${t('story.prompt_3')}...`}</Typography>,
      value: 'prompt_3'
    },
    {
      label: <Typography variant='body2'>{`${t('story.prompt_4')}...`}</Typography>,
      value: 'prompt_4'
    }
  ]
  const promptChange = (val) => {
    if (val === 'prompt_4') {
      setValue('content', '', { shouldValidate: true })
    } else {
      setValue('content', `${t(`story.${val}`)} `, { shouldValidate: true })
    }
  }
  return (
    <div className={classes.root}>
      <div>
        <Typography variant='h5'>{t('story.shareYourStory')}</Typography>
        <Typography variant='caption' component='p' style={{ marginBottom: '1.5em' }}>
          {t('story.estimate', { min: 3, max: 5 })}
        </Typography>
        <Typography variant='body1' component='p' style={{ marginBottom: '1.5em' }}>
          {/*
            * The signed-in greeting interpolates `first_name`. Someone who
            * scanned the card has no account, so there is no name to greet
            * and no attendance to thank them for -- a separate string rather
            * than the same one with a hole in it.
            */}
          {user
            ? t('story.welcomeHeader', { first_name: user.firstName, convo_topic: topic?.title, sponsor: topic?.partnerName })
            : t('story.welcomeHeaderGuest', { convo_topic: topic?.title, sponsor: topic?.partnerName })}
        </Typography>
        <Typography variant='body2' component='p'>
          {t('story.didYouKnow')}
        </Typography>
      </div>
      <Divider className={classes.divider} />
      <form onSubmit={handleSave}>
        <div>
          <Typography variant='body1' style={{ marginBottom: '1em' }}>
            {t('story.choosePrompt')}
          </Typography>
          <Typography variant='caption' component='p' style={{ marginBottom: '1em' }}>
            {t('story.selectOne')}
          </Typography>
          <Grid container className={classes.radioContainer}>
            <RadioGroupControl
              name='prompt'
              control={control}
              onChange={promptChange}
              RadioGroupWrapper={({ children, ...props }) => {
                return (
                  <RadioGroup {...props}>
                    <Grid container spacing={2} justify='center'>
                      {children}
                    </Grid>
                  </RadioGroup>
                )
              }}
              RadioWrapper={(props) => {
                return (
                  <Grid item xs={12} sm={6} md={6}>
                    <FormControlLabel {...props} className={classes.radio} />
                  </Grid>
                )
              }}
              options={PROMPT_EXAMPLES}
            />
          </Grid>
        </div>
        <Divider className={classes.divider} />
        <Typography variant='body1' component='p' style={{ marginBottom: '1em' }}>
          {t('story.addMedia')}
        </Typography>
        <Typography variant='body2' component='p' style={{ marginBottom: '0.5em' }}>
          {t('story.addMediaHelper')}
        </Typography>
        <Typography variant='caption' component='p' style={{ marginBottom: '1.5em' }}>
          {t('story.uploadFileHelper')}
        </Typography>
        <div className={classes.avatarImage}>
          <AvatarControl
            name='asset'
            accept='image/*,video/*'
            control={control}
            avatarSize={'profile-primary'}
            className={classes.preview}
            rounded={false}
            placeHolder={<MediaPlaceholder />}
          />
        </div>
        <Divider className={classes.divider} />
        <Typography variant='body1' style={{ marginBottom: '1em' }}>
          {t('story.addHeadline')}
        </Typography>
        <Typography variant='caption'>{t('story.addHeadlineHelper')}</Typography>
        <TextFieldControl
          name='headline'
          control={control}
          defaultValue={''}
          error={errors && errors.headline}
          required
          variant='outlined'
          className={`${classes.field} ${classes.sectionSpacer2}`}
          rules={{ validate: validations.required }}
        />
        <Typography variant='body1'>
          {t('story.storyFieldHeader')} <Typography variant='caption'>{t('story.storyFieldHeaderCaption')}</Typography>
        </Typography>
        <TextFieldControl
          name='content'
          control={control}
          defaultValue={''}
          error={errors && errors.content}
          required
          className={`${classes.field} ${classes.sectionSpacer2}`}
          variant='outlined'
          rules={{ validate: { required: validations.required, maxLength: validations.maxLength(2500) } }}
          multiline={true}
          rows={3}
        />
        {/*
          * How much room is left, so an over-long story is visible BEFORE the
          * submit rather than as a rejection afterwards. Pulled up into the
          * field's own bottom margin so it reads as part of the box.
          *
          * See CONTENT_MAX_CHARACTERS: this counts raw characters, which is
          * what the server measures, and the field's own rule measures the
          * text with whitespace stripped. On whitespace-heavy prose the
          * counter can therefore go red while the button is still enabled --
          * at which point the submit fails and the server's message lands
          * under the field.
          */}
        <Typography
          variant='caption'
          component='p'
          align='right'
          className={classes.counter}
          style={contentLength > CONTENT_MAX_CHARACTERS ? { color: '#C62828', fontWeight: 600 } : undefined}
        >
          {contentLength.toLocaleString()} / {CONTENT_MAX_CHARACTERS.toLocaleString()}
        </Typography>
        {/*
          * Who wrote it. Rendered only when nobody is signed in -- for a
          * signed-in author the server takes the name off the account, and
          * asking again would let the two disagree.
          *
          * Because these are not registered while a user is signed in,
          * react-hook-form leaves them out of the submitted values entirely,
          * and `isValid` does not wait on them.
          */}
        {!user && (
          <>
            <Divider className={classes.divider} />
            <Typography variant='body1' style={{ marginBottom: '1em' }}>
              {t('story.aboutYou')}
            </Typography>
            <Typography variant='caption' component='p' className={classes.sectionSpacer1}>
              {t('story.aboutYouHelper')}
            </Typography>
            <TextFieldControl
              name='author_name'
              label={t('story.yourName')}
              control={control}
              defaultValue={''}
              error={errors && errors.author_name}
              required
              variant='outlined'
              className={`${classes.field} ${classes.sectionSpacer2}`}
              rules={{ validate: { required: validations.required, maxLength: validations.maxLength(255) } }}
            />
            <TextFieldControl
              name='author_email'
              label={t('story.yourEmail')}
              control={control}
              defaultValue={''}
              error={errors && errors.author_email}
              required
              variant='outlined'
              className={`${classes.field} ${classes.sectionSpacer2}`}
              rules={{ validate: { required: validations.required, email: validations.email } }}
            />
          </>
        )}
        <Typography variant='caption' component='p' className={classes.sectionSpacer2}>
          {/*
            * A story sent with a card token arrives `visible: false` and waits
            * for an admin to approve it, so the standing note -- which
            * promises the story will post to the site -- is not true for one.
            * Keyed off the token rather than off the user because the token is
            * what decides it server-side.
            */}
          {submissionToken ? t('story.notePendingApproval', { sponsor: topic?.partnerName }) : t('story.note', { sponsor: topic?.partnerName })}
        </Typography>
        {submitError && (
          <Typography variant='body2' component='p' className={classes.submitError} role='alert'>
            {submitError}
          </Typography>
        )}
        <Button disabled={submitting || isSubmitting || !isValid} type='submit' color='primary' variant='contained' className={classes.sectionSpacer2}>
          {submissionToken ? t('story.submitStory') : t('story.publishStory')}
        </Button>
      </form>
    </div>
  )
}
const Styles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      marginTop: '3em'
    },
    radioContainer: {
      margin: '1em 0 2em 0'
    },
    field: {
      backgroundColor: '#ffffff'
    },
    divider: {
      backgroundColor: '#F7BF14',
      margin: '1.5em 0 2em 0',
      height: '2px'
    },
    avatarImage: {
      maxHeight: '184px'
    },
    radio: {
      backgroundColor: 'white',
      width: '100%',
      borderRadius: '10px',
      border: '1px solid #DFE0E2',
      marginLeft: 0,
      height: '100%',
      padding: '.2em 0'
    },
    sectionSpacer2: {
      marginBottom: '2em'
    },
    counter: {
      // Pulled up into the content field's own 2em bottom margin so the count
      // sits under the box rather than adrift below it.
      marginTop: '-1.5em',
      marginBottom: '1.5em',
      color: '#898A8E'
    },
    submitError: {
      color: '#C62828',
      marginBottom: '1em'
    },
    sectionSpacer1: {
      marginBottom: '1em'
    },
    preview: {
      backgroundColor: '#ff0000',
      maxHeight: '184px'
    }
  })
)
// export default flow(observer)(Form)

//  TODO: Move somewhere
const MediaPlaceholder = () => {
  return (
    <div style={{ backgroundColor: '#ffffff', padding: '2em', textAlign: 'center' }}>
      <i className='far fa-photo-video img-responsive' style={{ fontSize: '5em', margin: 'auto auto', color: '#898A8E' }}></i>
    </div>
  )
}

interface Props {
  onSubmit?: any
}
interface InjectedProps {
  stories: IStories
}
export default flow(
  observer,
  inject<InjectedProps, Props>(({ mst: { stories } }: { mst: IStore }, ownProps) => {
    return {
      stories,
      onSumbit: ownProps
    }
  }),
  withRouter
)(Form)
